import {
  AppointmentEventDto,
  AppointmentResourceOccupationDto,
  AuditState,
  NotificationDto,
  NotificationType,
  QueryFactory,
  UserClaims,
  VisitListDto,
} from '@lmt/shared-types';
import { PatientDataRequestToast } from '@lmt/unicorn-shared/app/components/patients/notifications/PatientDataRequestToast';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { extractTenantDomainFromUrl } from '@lmt/unicorn-shared/app/helpers/tenant-domain-helper';
import { UniStorageFactory } from '@lmt/unicorn-shared/app/helpers/uni-storage';
import { UniDispatch } from '@lmt/unicorn-shared/app/store/types';
import * as signalR from '@microsoft/signalr';
import {
  InvalidateOptions,
  QueryClient,
  useQueryClient,
} from '@tanstack/react-query';
import Logger from 'js-logger';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPatientIdFromPath } from '../../../navigation/components/NavTab';
import { ObjectChange } from './SignalRHandler.types';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { sendRefreshTokenRequest } from '@lmt/unicorn-shared/app/api/auth/auth-client';

const objectsChangedMethod = 'ObjectsChanged';
const notificationsMethod = 'SendNotification';
// We should not cancel existing refetching when receiving SignalR changes.
// Otherwise we have a bug in UIEditor when adding new DataBlockType:
//   we start refetch after we add new DataBlockType (because we need to select newly added DataBlockType)
//   but this refetching gets canceled by received SignalR notification
// Try to set it to true, because otherwise we have issues in MiniScreenImporter:
//   we create a new case when another GET /cases is already running.
//   so, old request is not canceled, and we do not get new cases
//When `cancelRefetch` set to false, no refetch will be made if there is already a request running.
const defaultInvalidateOptions: InvalidateOptions = { cancelRefetch: true };

export const SignalRHandler: FC = (props) => {
  const dispatch = useDispatch<UniDispatch>();
  const queryClient = useQueryClient();
  const claims = useClaims();
  const navigate = useNavigate();
  const i18next = useTranslation();

  const syncSettings = useMemo(
    () => syncSettingsCreator(dispatch, queryClient, navigate, claims),
    [dispatch, queryClient, claims],
  );

  const [activeConnection, setActiveConnection] = useState<
    signalR.HubConnection | undefined
  >(undefined);

  useEffect(() => {
    let connection: signalR.HubConnection | undefined;
    (async function connect() {
      try {
        connection = new signalR.HubConnectionBuilder()
          .withUrl(
            `${UniStorageFactory.UniStorage.BackendUri}/change-tracking-hub`,
          )
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: () => 2000,
          })
          .withKeepAliveInterval(20000)
          .build();
        connection.onreconnected(() => {
          Logger.info('SignalR reconnected');
        });
        connection
          .start()
          .then(() => setActiveConnection(connection))
          .catch(async (e) => {
            if (e.statusCode === 401) {
              console.log(
                'SignalR: received 401. Will send http request to refresh token.',
                e,
              );

              try {
                // send a request to automatically refresh the token
                await QueryFactory.VersionClient.versionAuthenticated();
              } catch (e) {
                console.error('Unable to establish SignalR connection', e);
                return;
              }
              connect().catch((e) =>
                console.error('Unable to establish SignalR connection2', e),
              );
              return;
            }
            console.error('Unable to establish SignalR connection', e);
          });
      } catch (e) {
        Logger.error(
          e,
          convertToErrorString(e),
          'Unable to connect via SignalR',
        );
      }
    })().catch((e) => Logger.error(e));
    return () => {
      connection?.stop().catch(console.warn);
    };
  }, []);

  const processChanges = useCallback(
    async (changes: ObjectChange[]) => {
      for (const change of changes) {
        const ignoreChange = ignoreChanges[change.type];
        if (
          ignoreChange &&
          new Date().getTime() < ignoreChange.ignoreTill.getTime()
        ) {
          if (ignoreChange.ignoreFunction) {
            if (ignoreChange.ignoreFunction(change)) {
              continue;
            }
          } else {
            continue;
          }
        }

        const settings = syncSettings[change.type];
        if (settings) {
          await (settings as any).onChange?.(change as any);
        } else {
          console.error(`Unknown type of changed object: ${change.type}`);
        }
      }
    },
    [syncSettings],
  );

  useEffect(() => {
    if (activeConnection) {
      const handleChange = (changes: Array<ObjectChange>) => {
        processChanges(changes).catch((e) => Logger.error(e));
      };
      const sendNotification = (object: NotificationDto) => {
        if (
          object.type === NotificationType.PatientDataReceived &&
          object.patientId &&
          object.userId === claims!.id
        ) {
          toast.dismiss();
          toast(<PatientDataRequestToast patientId={object.patientId} />);
        }
        if (
          object.type === NotificationType.HL7MessageWasAutoCreated &&
          object.userId === claims!.id
        ) {
          toast.dismiss();
          toast(i18next.t('patients.notifications.hl7_message_created'), {
            type: 'success',
          });
        }
        if (
          object.type === NotificationType.GDTMessageWasAutoCreated &&
          object.userId === claims!.id
        ) {
          toast.dismiss();
          toast(i18next.t('patients.notifications.gdt_message_created'), {
            type: 'success',
          });
        }
      };
      activeConnection.on(objectsChangedMethod, handleChange);
      activeConnection.on(notificationsMethod, sendNotification);
      return () => {
        activeConnection.off(objectsChangedMethod, handleChange);
        activeConnection.off(notificationsMethod, sendNotification);
      };
    }
    return undefined;
  }, [activeConnection, processChanges]);

  return null;
};

type SyncSettingHandlers = {
  [key in ObjectChange['type']]: {
    onChange: (change: ObjectChange & { type: key }) => void;
  };
};

let pauseReconnect = 0;
/*
 * Calling this method prevents query invalidation based on SignalR requests.
 * Could be used to prevent unnecessary invalidations if another tab/case will be opened as a result of an action
 * (and requests from the old tab do not need to be invalidated)
 * Best example: creating new case
 */
export function pauseQueryInvalidationRequests() {
  pauseReconnect++;
  return () => {
    pauseReconnect--;
  };
}

const invalidateQueriesSignalR: QueryClient['invalidateQueries'] =
  async function (this: QueryClient, ...args) {
    if (pauseReconnect > 0) return;
    this.invalidateQueries(...args);
  };

const syncSettingsCreator = (
  dispatch: UniDispatch,
  queryClient: QueryClient,
  navigate: NavigateFunction,
  claims: UserClaims | null,
): SyncSettingHandlers => {
  const invalidateQueriesSignalRLocal: QueryClient['invalidateQueries'] =
    invalidateQueriesSignalR.bind(queryClient);

  const invalidateVisits = () => [
    invalidateQueriesSignalRLocal(
      {
        queryKey:
          QueryFactory.AppointmentEventsQuery.searchAppointmentsQueryKey(),
      },
      defaultInvalidateOptions,
    ),
    invalidateQueriesSignalRLocal(
      {
        queryKey: QueryFactory.AppointmentEventsQuery.searchVisitsQueryKey(),
      },
      defaultInvalidateOptions,
    ),
    invalidateQueriesSignalRLocal(
      {
        queryKey:
          QueryFactory.AppointmentEventsQuery.searchAppointmentsWithResourceOccupationQueryKey(),
      },
      defaultInvalidateOptions,
    ),
  ];
  function invalidatePermissions() {
    invalidateQueriesSignalRLocal({
      queryKey: QueryFactory.UserQuery.getCurrentUserPermissionsQueryKey(),
    });
  }

  return {
    User: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.UserQuery.getUsersQueryKey(),
          },
          defaultInvalidateOptions,
        );
        // if current user is updated, their roles could be changed
        if (claims && change.object.id == claims.id && change.operation) {
          invalidatePermissions();
        }
      },
    },
    GDTTemplateRule: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.GDTTemplateRulesQuery.getQueryKey(),
          },
          defaultInvalidateOptions,
        );
      },
    },
    HL7TemplateRule: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.HL7TemplateRulesQuery.getQueryKey(),
          },
          defaultInvalidateOptions,
        );
      },
    },
    DataBlockField: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.DataBlockFieldQuery.getQueryKey(),
          },
          defaultInvalidateOptions,
        );
      },
    },
    TenantSetting: {
      onChange: (change) => {
        if (change.object.id === 'GDTSettings') {
          void invalidateQueriesSignalRLocal(
            {
              queryKey: QueryFactory.GDTSettingsQuery.getQueryKey(),
            },
            defaultInvalidateOptions,
          );
        } else if (change.object.id === 'HL7Settings') {
          void invalidateQueriesSignalRLocal(
            {
              queryKey: QueryFactory.HL7SettingsQuery.getQueryKey(),
            },
            defaultInvalidateOptions,
          );
        } else if (change.object.id === 'CalendarSettings') {
          void invalidateQueriesSignalRLocal(
            {
              queryKey: QueryFactory.CalendarSettingsQuery.getQueryKey(),
            },
            defaultInvalidateOptions,
          );
        } else if (change.object.id === 'VisitCardSettings') {
          void invalidateQueriesSignalRLocal(
            {
              queryKey: QueryFactory.VisitCardSettingsQuery.getQueryKey(),
            },
            defaultInvalidateOptions,
          );
        } else if (change.object.id === 'AppointmentColorSettings') {
          void invalidateQueriesSignalRLocal(
            {
              queryKey:
                QueryFactory.AppointmentColorSettingsQuery.getQueryKey(),
            },
            defaultInvalidateOptions,
          );
        } else if (change.object.id === 'MeasurementImportSettings') {
          void invalidateQueriesSignalRLocal(
            {
              queryKey:
                QueryFactory.MeasurementImportSettingsQuery.getQueryKey(),
            },
            defaultInvalidateOptions,
          );
        }
      },
    },
    Role: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.RoleQuery.getRolesQueryKey(),
          },
          defaultInvalidateOptions,
        );
        // if roles have been updated, refresh user's permissions
        invalidatePermissions();
      },
    },
    Subtenant: {
      onChange: async (change) => {
        if (
          claims &&
          (change.operation == AuditState.Added ||
            change.operation == AuditState.Deleted)
        ) {
          await sendRefreshTokenRequest();
        }

        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.TenantQuery.getSubtenantsQueryKey(),
          },
          defaultInvalidateOptions,
        );
      },
    },

    DictVisitType: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.VisitTypeQuery.getQueryKey(),
          },
          defaultInvalidateOptions,
        );
      },
    },
    Patient: {
      onChange: (change) => {
        const operation = change.operation;
        if (operation === AuditState.Deleted) {
          const patientIdFromPath = getPatientIdFromPath(window.location.href);
          if (patientIdFromPath === change.object.id) {
            navigate(pages.dashboard.link());
          }
          dispatch(NavigationActions.patientRemoved(change.object.id));
          void invalidateVisits();
        } else {
          void invalidateQueriesSignalRLocal({
            queryKey: QueryFactory.PatientQuery.getPatientQueryKey(
              change.object.id,
            ),
          });
        }
        void invalidateQueriesSignalRLocal({
          queryKey: QueryFactory.PatientQuery.getPatientsQueryKey(),
        });

        void invalidateQueriesSignalRLocal({
          queryKey: QueryFactory.AppointmentEventsQuery.searchVisitsQueryKey(),
        });
      },
    },
    Visit: {
      onChange: async (change) => {
        invalidateVisits();
        invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.AppointmentEventsQuery.getVisitQueryKey(
              change.object.id,
            ),
          },
          defaultInvalidateOptions,
        );
      },
    },
    Absence: {
      onChange: async (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey:
              QueryFactory.AppointmentEventsQuery.searchAppointmentsQueryKey(),
          },
          defaultInvalidateOptions,
        );
        void invalidateQueriesSignalRLocal(
          {
            queryKey:
              QueryFactory.AppointmentEventsQuery.searchAppointmentsWithResourceOccupationQueryKey(),
          },
          defaultInvalidateOptions,
        );
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.AppointmentEventsQuery.getAbsenceQueryKey(
              change.object.id,
            ),
          },
          defaultInvalidateOptions,
        );
      },
    },
    DataBlockType: {
      onChange: async (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.DataBlockTypeQuery.getQueryKey(),
          },
          { cancelRefetch: false },
        );
      },
    },
    Note: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.NoteQuery.getQueryKey(),
          },
          defaultInvalidateOptions,
        );
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.NoteQuery.getNoteQueryKey(change.object.id),
          },
          defaultInvalidateOptions,
        );
      },
    },
    Case: {
      onChange: (change) => {
        // without this if we get Bug 39988: Trying to get a case after deleting it
        if (change.operation !== AuditState.Deleted) {
          void invalidateQueriesSignalRLocal(
            {
              queryKey: QueryFactory.CaseQuery.getCaseQueryKey(
                change.object.id,
              ),
            },
            defaultInvalidateOptions,
          );
        }
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.CaseQuery.getCasesQueryKey({
              patientId: change.object.patientId,
            }),
          },
          defaultInvalidateOptions,
        );
      },
    },
    GDTMessage: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.GDTMessageQuery.getMessagesQueryKey(),
          },
          defaultInvalidateOptions,
        );
      },
    },
    HL7Message: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.FileQuery.searchFilesQueryKey(),
          },
          defaultInvalidateOptions,
        );
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.HL7MessageQuery.getMessagesQueryKey(),
          },
          defaultInvalidateOptions,
        );
      },
    },
    DictTag: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.TagQuery.getQueryKey(),
          },
          defaultInvalidateOptions,
        );
      },
    },
    DataBlockDictionaryItem: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
              change.object.dictionaryId,
            ),
          },
          defaultInvalidateOptions,
        );
      },
    },
    Doctor: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.DoctorQuery.getDoctorQueryKey(
              change.object.id,
            ),
          },
          defaultInvalidateOptions,
        );
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.DoctorQuery.getDoctorsQueryKey(),
          },
          defaultInvalidateOptions,
        );
        if (change.object.dictionaryId) {
          void invalidateQueriesSignalRLocal({
            queryKey: QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
              change.object.dictionaryId,
            ),
          });
        }
      },
    },
    DataBlock: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.DataBlockQuery.getQueryKey(change.object.id),
          },
          defaultInvalidateOptions,
        );
        if (change.operation === AuditState.Added) {
          if (change.object.visitId) {
            void invalidateVisitQueriesContainingVisitId(
              invalidateQueriesSignalRLocal,
              change.object.visitId,
            );
          }
          if (change.object.caseId) {
            void invalidateQueriesSignalRLocal(
              {
                queryKey: QueryFactory.CaseQuery.getCaseQueryKey(
                  change.object.caseId,
                ),
              },
              defaultInvalidateOptions,
            );
          }
        }
        if (
          change.operation === AuditState.Added ||
          change.operation === AuditState.Deleted
        ) {
          void invalidateQueriesSignalRLocal(
            {
              queryKey:
                QueryFactory.DataBlockTypeQuery.getDataBlockTypesForEditingQueryKey(),
            },
            defaultInvalidateOptions,
          );
        }
      },
    },
    Resource: {
      onChange: (change) => {
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.ResourceQuery.getResourceQueryKey(
              change.object.id,
            ),
          },
          defaultInvalidateOptions,
        );
        void invalidateQueriesSignalRLocal(
          {
            queryKey: QueryFactory.ResourceQuery.getResourcesQueryKey(),
          },
          defaultInvalidateOptions,
        );
        if (change.object.doctorId) {
          void invalidateQueriesSignalRLocal(
            {
              queryKey: QueryFactory.DoctorQuery.getDoctorsQueryKey(),
            },
            defaultInvalidateOptions,
          );
          void invalidateQueriesSignalRLocal(
            {
              queryKey: QueryFactory.DoctorQuery.getDoctorQueryKey(
                change.object.doctorId,
              ),
            },
            defaultInvalidateOptions,
          );
        }
      },
    },
  };
};

function invalidateVisitQueriesContainingVisitId(
  invalidateQueriesSignalRLocal: QueryClient['invalidateQueries'],
  visitId: number,
) {
  void invalidateQueriesSignalRLocal(
    {
      queryKey: QueryFactory.AppointmentEventsQuery.getVisitQueryKey(visitId),
    },
    defaultInvalidateOptions,
  );

  void invalidateQueriesSignalRLocal(
    {
      queryKey:
        QueryFactory.AppointmentEventsQuery.searchAppointmentsQueryKey(),
      predicate: (query) => {
        const data = query.state.data as AppointmentEventDto[];
        if (!data) return false;
        return !!data.find((x) => x.id === visitId);
      },
    },
    defaultInvalidateOptions,
  );
  void invalidateQueriesSignalRLocal(
    {
      queryKey: QueryFactory.AppointmentEventsQuery.searchVisitsQueryKey(),
      predicate: (query) => {
        const data = query.state.data as VisitListDto[];
        if (!data) return false;
        return !!data.find((x) => x.id === visitId);
      },
    },
    defaultInvalidateOptions,
  );
  void invalidateQueriesSignalRLocal(
    {
      queryKey:
        QueryFactory.AppointmentEventsQuery.searchAppointmentsWithResourceOccupationQueryKey(),
      predicate: (query) => {
        const data = query.state.data as AppointmentResourceOccupationDto[];
        if (!data) return false;
        return !!data.find((x) => x.id === visitId);
      },
    },
    defaultInvalidateOptions,
  );
}

const ignoreChanges: {
  [key in ObjectChange['type']]: {
    ignoreTill: Date;
    ignoreFunction?: (change: ObjectChange) => boolean;
  };
} = {} as any;
export function ignoreSignalRChanges(
  type: ObjectChange['type'],
  ignoreTill: Date,
  options?: { ignoreFunction?: (change: ObjectChange) => boolean },
) {
  ignoreChanges[type] = { ignoreTill, ignoreFunction: options?.ignoreFunction };
  return () => {
    delete ignoreChanges[type];
  };
}
